import { Loader2, Inbox, AlertTriangle, Lock } from "lucide-react";

export function Loading({ label = "Loading...", className = "" }) {
  return (
    <div data-testid="loading-state" className={`flex flex-col items-center justify-center py-20 text-ink/50 ${className}`}>
      <Loader2 className="h-7 w-7 animate-spin text-loop" strokeWidth={2} />
      <p className="mt-3 text-sm font-medium">{label}</p>
    </div>
  );
}

export function EmptyState({ title = "Nothing here yet", subtitle, icon: Icon = Inbox, action }) {
  return (
    <div data-testid="empty-state" className="flex flex-col items-center justify-center py-16 text-center">
      <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-mist text-loop">
        <Icon className="h-6 w-6" strokeWidth={1.75} />
      </div>
      <h3 className="mt-4 font-heading text-lg font-semibold text-navy">{title}</h3>
      {subtitle && <p className="mt-1 max-w-sm text-sm text-ink/60">{subtitle}</p>}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}

// Subtle content-area overlay shown while a refresh is in flight (filter apply,
// chip click, search, saved view) so the user gets immediate feedback even if
// the result list is far down the page. Sits over the content, not the navbar.
export function LoadingOverlay({ show, label = "Updating…" }) {
  if (!show) return null;
  return (
    <div data-testid="loading-overlay" className="pointer-events-none absolute inset-0 z-20 flex items-start justify-center rounded-2xl bg-white/55 pt-24 backdrop-blur-[1px]">
      <div className="flex items-center gap-2 rounded-full border border-sky bg-white px-4 py-2 shadow-md">
        <Loader2 className="h-4 w-4 animate-spin text-loop" />
        <span className="text-sm font-medium text-ink/70">{label}</span>
      </div>
    </div>
  );
}

export function ErrorView({ message = "Something went wrong.", onRetry }) {
  return (
    <div data-testid="error-state" className="flex flex-col items-center justify-center py-16 text-center">
      <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-red-50 text-red-500">
        <AlertTriangle className="h-6 w-6" strokeWidth={1.75} />
      </div>
      <h3 className="mt-4 font-heading text-lg font-semibold text-navy">Couldn't load this</h3>
      <p className="mt-1 max-w-sm text-sm text-ink/60">{message}</p>
      {onRetry && (
        <button
          data-testid="error-retry-button"
          onClick={onRetry}
          className="mt-5 rounded-lg bg-loop px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-loophover"
        >
          Try again
        </button>
      )}
    </div>
  );
}

export function Forbidden() {
  return (
    <EmptyState
      icon={Lock}
      title="Access restricted"
      subtitle="You don't have permission to view this page. Contact your admin if you think this is a mistake."
    />
  );
}
